import React, { useEffect, useState } from "react";

import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import axiosGroceryInstance from "../restaurant/AxiosGroceryInstance";
import axiosOrganizationInstance from "../organization/AxiosOrganizationInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import {
  GROCERY_API_PATHS,
  ORGANIZATION_API_PATHS,
  RESTAURANT_API_PATHS,
} from "../constants/apiPaths";
import {
  formatAadharDisplay,
  formatAadharDisplayOrFallback,
  sanitizeAadharInput,
  sanitizeMobileInput,
  validateAadharNumber,
  validateMobileNumber,
} from "../restaurant/formatDisplay";
import RootAdminUserPicker from "./RootAdminUserPicker";
import "./EatsAdminCreatePage.css";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  aadharNo: "",
  password: "",
};

function resolveEntityType({ entityType, restaurantId }) {
  if (entityType) return entityType;
  if (restaurantId != null) return "restaurant";
  return "restaurant";
}

function resolveEntityId({ entityId, restaurantId, storeId, organizationId }, type) {
  if (entityId != null) return entityId;
  if (type === "restaurant") return restaurantId;
  if (type === "store") return storeId;
  if (type === "organization") return organizationId;
  return null;
}

function resolveEntityName({ entityName, restaurantName, storeName, organizationName }, type) {
  if (entityName) return entityName;
  if (type === "restaurant") return restaurantName || "";
  if (type === "store") return storeName || "";
  if (type === "organization") return organizationName || "";
  return "";
}

const RootAdminAssignPanel = ({
  entityType: entityTypeProp,
  entityId: entityIdProp,
  entityName: entityNameProp,
  restaurantId,
  restaurantName,
  storeId,
  storeName,
  organizationId,
  organizationName,
  createdById,
  title,
  onSuccess,
}) => {
  const entityType = resolveEntityType({ entityType: entityTypeProp, restaurantId });
  const entityId = resolveEntityId(
    { entityId: entityIdProp, restaurantId, storeId, organizationId },
    entityType
  );
  const entityName = resolveEntityName(
    { entityName: entityNameProp, restaurantName, storeName, organizationName },
    entityType
  );

  const [mode, setMode] = useState("existing");
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orgRoleId, setOrgRoleId] = useState(null);
  const [orgRoleName, setOrgRoleName] = useState("ROOT_ADMIN");

  useEffect(() => {
    if (entityType !== "organization") return undefined;

    let cancelled = false;
    const loadRoles = async () => {
      try {
        const res = await axiosOrganizationInstance.get("/organization/roles", {
          headers: getAuthHeaders(),
        });
        if (cancelled) return;
        const roles = Array.isArray(res?.data?.roles) ? res.data.roles : [];
        const apiRoot = res?.data?.rootAdminRole;
        if (apiRoot?.Role_Id != null) {
          setOrgRoleId(apiRoot.Role_Id);
          setOrgRoleName(apiRoot.Role_Name || "ROOT_ADMIN");
          return;
        }
        const rootRole =
          roles.find((r) => String(r.Role_Name || r.roleName || "").toUpperCase() === "ROOT_ADMIN") ||
          roles.find((r) => String(r.Role_Name || r.roleName || "").toUpperCase() === "ADMIN");
        if (rootRole) {
          setOrgRoleId(rootRole.Role_Id ?? rootRole.roleId);
          setOrgRoleName(rootRole.Role_Name || rootRole.roleName || "ROOT_ADMIN");
        }
      } catch (error) {
        console.error("Failed loading organization roles:", error?.response?.data || error?.message);
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [entityType]);

  const reset = () => {
    setForm(EMPTY_FORM);
    setSelectedUser(null);
    setMode("existing");
  };

  const handleSelectExisting = (user) => {
    setSelectedUser(user);
    setMode("existing");
    const nameParts = String(user?.name || "").trim().split(/\s+/);
    setForm({
      ...EMPTY_FORM,
      firstName: user?.firstName || nameParts[0] || "",
      lastName: user?.lastName || nameParts.slice(1).join(" ") || "",
      mobile: user?.mobile || user?.contactNumber || "",
      email: user?.email || "",
      aadharNo: user?.aadharNo || "",
      password: "",
    });
  };

  const handleCreateNew = () => {
    setSelectedUser(null);
    setForm(EMPTY_FORM);
    setMode("new");
  };

  const handleBackToSearch = () => {
    setSelectedUser(null);
    setForm(EMPTY_FORM);
    setMode("existing");
  };

  const getEntityConfig = () => {
    if (entityType === "store") {
      return {
        axiosInstance: axiosGroceryInstance,
        searchPath: GROCERY_API_PATHS.storeRootAdminUsers,
        usersPath: GROCERY_API_PATHS.storeUsers,
        typeFilter: null,
        requireEmail: false,
        entityLabel: "store",
        createLabel: "Create New Owner",
        buildAssignPayload: (user) => ({
          userId: user.userId,
          roleName: "ROOT_ADMIN",
          storeId: entityId,
          createdById,
        }),
        buildCreatePayload: ({ firstName, lastName, mobile, email, aadharNo, password }) => ({
          name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          firstName,
          lastName,
          mobile,
          email,
          aadharNumber: sanitizeAadharInput(aadharNo),
          password,
          roleName: "ROOT_ADMIN",
          storeId: entityId,
          createdById,
        }),
      };
    }
    if (entityType === "organization") {
      return {
        axiosInstance: axiosOrganizationInstance,
        searchPath: ORGANIZATION_API_PATHS.organizationRootAdminUsers,
        usersPath: ORGANIZATION_API_PATHS.organizationUser,
        typeFilter: null,
        requireEmail: true,
        entityLabel: "organization",
        createLabel: "Create New Owner",
        buildAssignPayload: (user) => ({
          userId: user.userId,
          roleId: orgRoleId,
          roleName: orgRoleName,
          organizationId: entityId,
          organizationName: entityName,
          createdById,
        }),
        buildCreatePayload: ({ firstName, lastName, mobile, email, aadharNo, password }) => ({
          name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          email: email.trim(),
          contactNumber: mobile.trim(),
          aadharNumber: sanitizeAadharInput(aadharNo),
          password,
          roleId: orgRoleId,
          roleName: orgRoleName,
          organizationId: entityId,
          organizationName: entityName,
          createdById,
        }),
      };
    }
    return {
      axiosInstance: axiosRestaurantInstance,
      searchPath: RESTAURANT_API_PATHS.restaurantRootAdminUsers,
      usersPath: RESTAURANT_API_PATHS.restaurantsUsers,
      typeFilter: "RST",
      requireEmail: false,
      entityLabel: "restaurant",
      createLabel: "Create New Owner",
      buildAssignPayload: (user) => ({
        userId: user.userId,
        roleNames: ["ROOT_ADMIN"],
        restaurantId: entityId,
        restaurantName: entityName,
        createdById,
        status: "ACTIVE",
      }),
      buildCreatePayload: ({ firstName, lastName, mobile, email, aadharNo, password }) => ({
        firstName,
        lastName,
        mobile,
        email,
        aadharNo: sanitizeAadharInput(aadharNo),
        password,
        roleNames: ["ROOT_ADMIN"],
        restaurantId: entityId,
        restaurantName: entityName,
        createdById,
        status: "ACTIVE",
      }),
    };
  };

  const handleSave = async () => {
    const config = getEntityConfig();
    if (!entityId) {
      alert(`Please create a ${config.entityLabel} first.`);
      return;
    }
    if (!createdById) {
      alert("Current user is missing. Please sign in again.");
      return;
    }
    if (entityType === "organization" && (orgRoleId == null || orgRoleId === "")) {
      alert("Organization admin role is not configured. Restart company API and try again.");
      return;
    }

    const assigningExisting = mode === "existing" && selectedUser?.userId;
    const { firstName, lastName, mobile, email, aadharNo, password } = form;

    if (assigningExisting) {
      setSaving(true);
      try {
        const response = await config.axiosInstance.post(
          config.usersPath,
          config.buildAssignPayload(selectedUser),
          { headers: getAuthHeaders() }
        );
        alert(response?.data?.message || "Owner assigned successfully");
        reset();
        onSuccess?.({ assigned: true, entityType, entityId });
      } catch (error) {
        alert(error?.response?.data?.message || "Failed to assign owner");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!firstName || !lastName || !mobile || !aadharNo?.trim() || !password) {
      alert("Please fill first name, last name, personal mobile, aadhar number and password.");
      return;
    }
    if (config.requireEmail && !email?.trim()) {
      alert("Please fill email.");
      return;
    }
    const mobileError = validateMobileNumber(mobile, { required: true, label: "Personal Mobile" });
    if (mobileError) {
      alert(mobileError);
      return;
    }
    const aadharError = validateAadharNumber(aadharNo, { required: true, label: "Aadhar number" });
    if (aadharError) {
      alert(aadharError);
      return;
    }

    setSaving(true);
    try {
      const response = await config.axiosInstance.post(
        config.usersPath,
        config.buildCreatePayload(form),
        { headers: getAuthHeaders() }
      );
      alert(response?.data?.message || "Owner created successfully");
      reset();
      onSuccess?.({ created: true, entityType, entityId });
    } catch (error) {
      alert(error?.response?.data?.message || "Failed to create owner");
    } finally {
      setSaving(false);
    }
  };

  const config = getEntityConfig();

  return (
    <div className="eats-admin-root-admin">
      {title ? <h3 className="eats-admin-root-admin__title">{title}</h3> : null}

      {mode === "existing" ? (
        <RootAdminUserPicker
          axiosInstance={config.axiosInstance}
          apiPath={config.searchPath}
          selectedUserId={selectedUser?.userId}
          onSelect={handleSelectExisting}
          onClear={handleBackToSearch}
          autoLoad={false}
          requireQuery
          typeFilter={config.typeFilter}
          toolbarRight={
            <button
              type="button"
              className="eats-admin-btn eats-admin-btn--create"
              onClick={handleCreateNew}
            >
              Create New
            </button>
          }
        />
      ) : (
        <div className="eats-admin-root-admin__create-bar">
          <button
            type="button"
            className="eats-admin-root-admin__back-search"
            onClick={handleBackToSearch}
          >
            Search existing
          </button>
          <span className="eats-admin-root-admin__create-label">{config.createLabel}</span>
        </div>
      )}

      {mode === "new" ? (
        <div className="eats-admin-form-grid">
          <input
            placeholder="First Name *"
            value={form.firstName}
            onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
          />
          <input
            placeholder="Last Name *"
            value={form.lastName}
            onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
          />
          <input
            placeholder="Personal Mobile *"
            value={form.mobile}
            onChange={(e) => setForm((p) => ({ ...p, mobile: sanitizeMobileInput(e.target.value) }))}
            maxLength={10}
            inputMode="numeric"
          />
          <input
            type="email"
            placeholder={config.requireEmail ? "Email *" : "Email"}
            value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          />
          <input
            placeholder="Aadhar Number *"
            value={formatAadharDisplay(form.aadharNo)}
            onChange={(e) => setForm((p) => ({ ...p, aadharNo: sanitizeAadharInput(e.target.value) }))}
            maxLength={14}
            inputMode="numeric"
          />
          <input
            type="password"
            placeholder="Password *"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          />
        </div>
      ) : selectedUser ? (
        <div className="eats-admin-selected-user">
          Selected:{" "}
          <strong>
            {selectedUser.name || `${form.firstName} ${form.lastName}`.trim()}
          </strong>
          {" · "}Aadhar:{" "}
          <strong>{formatAadharDisplayOrFallback(selectedUser.aadharNo || form.aadharNo)}</strong>
          {" · "}Personal Mobile:{" "}
          <strong>{selectedUser.mobile || form.mobile || "—"}</strong>
        </div>
      ) : (
        <div className="eats-admin-hint">
          Search by name or Aadhar number and select a user, or use Create New.
        </div>
      )}

      <div className="eats-admin-actions">
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--primary"
          onClick={handleSave}
          disabled={saving || (mode === "existing" && !selectedUser?.userId)}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default RootAdminAssignPanel;
